import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';

const APP_STORE_URL = 'https://apps.apple.com/app/id6754500015';
const SITE_URL = 'https://kidquotes.vercel.app';

interface ContentTemplate {
  title: string;
  content: string;
  platform?: string;
}

const instagramTemplates: ContentTemplate[] = [
  {
    title: 'Emotional Hook',
    content: `Kids say the most incredible things — and we forget 90% of them.

KidQuotes is a free app that lets you capture those priceless moments in seconds, right when they happen.

Because one day they'll stop saying "I love you to the moon and back to the moon again" and you'll want to remember exactly how they said it.

Link in bio.

#kidquotes #thingskidssay #momlife #dadlife #parentingwin #childhoodmemories #kidsarethebest #raisingkids #parenthood #familymoments`,
  },
  {
    title: 'Funny Quote Format',
    content: `My 4-year-old just said: "Mom, I can't eat this. It's too spicy for my feelings."

I NEED to write these down before I forget them. That's why I use KidQuotes — it takes 5 seconds to save a quote and I'll have them forever.

Free app, link in bio.

#kidsaythedarndestthings #funnykids #toddlerlife #momhumor #dadhumor #parentinghumor #kidquotes #childhoodunplugged`,
  },
  {
    title: 'Milestone Moment',
    content: `First steps. First words. First time they tell you they love you unprompted.

These milestones fly by. KidQuotes helps you capture not just WHAT they said, but WHEN — so you can look back and relive it all.

Free on the App Store. Link in bio.

#babymilestones #firstwords #momentslikethese #parentingjourney #kidquotes #memoriesforlife #growinguptoofast`,
  },
  {
    title: 'Seasonal - Back to School',
    content: `Back to school means back to hilarious quotes about teachers, homework, and cafeteria food.

Don't let this school year's best one-liners slip away. Save them in KidQuotes.

Free on the App Store. Link in bio.

#backtoschool #schoollife #kidquotes #funnykids #parentlife #schoolmemes`,
  },
  {
    title: 'Seasonal - Holidays',
    content: `The holidays bring out the BEST kid quotes.

"Why does Santa use the chimney? Doesn't he know about doors?"

Start capturing your family's holiday gems with KidQuotes — they make the best stories at next year's dinner table.

Free on the App Store.

#holidayseason #kidsatchristmas #familytraditions #kidquotes #holidaymemories`,
  },
  {
    title: 'Family Sharing Feature',
    content: `The best part about KidQuotes? You can share a family account so grandparents, aunts, uncles — everyone can add quotes when they hear something amazing.

Because the best quotes always happen when you're not in the room.

Free on the App Store. Link in bio.

#familyapp #grandparentslife #kidquotes #familymemories #multigenerational`,
  },
];

const twitterTemplates: ContentTemplate[] = [
  {
    title: 'Quick Hook',
    content: `My kid: "Dad, why do we have to sleep? Can't we just close our eyes and pretend?"

I save every gem like this in KidQuotes. Free app, takes 5 seconds. ${APP_STORE_URL}`,
  },
  {
    title: 'Relatable Parent',
    content: `You think you'll remember what your kid said. You won't.

Write it down. Or better yet, use KidQuotes — it's free and takes 5 seconds.

${APP_STORE_URL}`,
  },
  {
    title: 'Stats Hook',
    content: `Kids ask ~300 questions a day. Some of them are absolute gold.

KidQuotes — save the funny, sweet, and wild things your kids say before you forget.

Free: ${APP_STORE_URL}`,
  },
  {
    title: 'Thread Starter',
    content: `What's the funniest thing your kid has ever said? I'll go first:

My 3yo, whispering to the dog: "Don't worry. I'll steal you some chicken."

(I save all of these in KidQuotes btw — free app, highly recommend) ${APP_STORE_URL}`,
  },
  {
    title: 'Nostalgia',
    content: `My "On This Day" memory in KidQuotes just showed me a quote from my daughter at age 2. She's 6 now.

Save the little moments. They become the big ones.

${APP_STORE_URL}`,
  },
];

const redditTemplates: ContentTemplate[] = [
  {
    title: 'r/Parenting - Discussion Prompt',
    content: `**What's the funniest thing your kid said this week?**

I've been making a habit of writing down the hilarious things my kids say and it's honestly become my favorite thing. My 4yo told me yesterday that "butterflies are just flowers that got bored."

What gems have your kids dropped recently? Would love to hear them.`,
  },
  {
    title: 'r/daddit or r/Mommit - Tip Post',
    content: `**PSA: Write down the things your kids say. You will NOT remember them.**

I started doing this about a year ago and just went back and read through everything. I was crying laughing at stuff I had completely forgotten.

I use an app called KidQuotes on iOS but honestly even a notes app works. Just get in the habit. Future you will be so grateful.`,
  },
  {
    title: 'r/Parenting - Reflection Post',
    content: `**The little things they say hit different when you read them later**

My daughter is 5 now. I've been saving her quotes since she was 2. Just went through them and some highlights:

- Age 2: "Daddy, the moon is following us. Tell it to stop."
- Age 3: "I can't wear that shirt. It doesn't match my feelings."
- Age 4: "When I grow up I want to be a dog. Or a doctor. Or a dog doctor."

If you're not already writing these down, seriously start. You think you'll remember but you won't.`,
  },
  {
    title: 'r/daddit - App Recommendation (soft)',
    content: `**How do you keep track of the funny things your kids say?**

I tried using Notes, a journal, even voice memos but nothing stuck. Finally found a dedicated app (KidQuotes) that makes it stupid easy — literally open, type the quote, done.

The best part is the "On This Day" feature that surfaces old quotes. My wife and I read them together and it's become a nightly ritual.

Curious what other parents use? Any other good methods?`,
  },
];

const blogTemplates: ContentTemplate[] = [
  {
    title: '50 Funniest Things Kids Say',
    content: `**Blog Post Outline: "50 Hilarious Things Kids Actually Said (That Parents Will Never Forget)"**

1. **Intro** (200 words)
   - Kids say the most unexpectedly profound and hilarious things
   - We asked parents to share their favorites
   - Mention KidQuotes as a way to save these moments

2. **The Quotes** (organized by category)
   - Food commentary (10 quotes)
   - Bedtime negotiations (10 quotes)
   - Questions about the world (10 quotes)
   - Accidental roasts of parents (10 quotes)
   - Surprisingly deep observations (10 quotes)

3. **Why You Should Write These Down** (200 words)
   - Memory is unreliable
   - These become family treasures
   - Great for scrapbooks, speeches, future embarrassment

4. **CTA**: Download KidQuotes to start capturing your own collection

**SEO keywords**: funny things kids say, kid quotes, things kids say, funny kid moments, parenting humor`,
  },
  {
    title: 'Why You Should Journal Kid Quotes',
    content: `**Blog Post Outline: "Why Every Parent Should Write Down What Their Kids Say"**

1. **The Problem** (200 words)
   - Kids say ~300 things a day
   - We forget 95% within a week
   - The most precious moments are fleeting

2. **The Science of Memory** (300 words)
   - How quickly we forget details
   - Why written records matter more than we think
   - The emotional value of nostalgia

3. **How to Build the Habit** (400 words)
   - Keep it simple (apps like KidQuotes, or a notebook)
   - Capture immediately — don't wait
   - Include context (what was happening, their age)
   - Share with family (multiple perspectives catch more)

4. **The Payoff** (200 words)
   - Reading old quotes years later
   - Sharing at graduations, weddings
   - Creating printed quote books
   - Building a family legacy

5. **CTA**: Start today with KidQuotes — free on the App Store

**SEO keywords**: parenting journal, kid quotes, childhood memories, things kids say, family journal app`,
  },
  {
    title: 'Best Apps for Childhood Memories',
    content: `**Blog Post Outline: "The 5 Best Apps for Preserving Childhood Memories in 2026"**

1. **Intro** (200 words)
   - Technology can help us hold onto fleeting moments
   - Overview of different approaches (photos, journals, quotes)

2. **App Reviews**
   - **KidQuotes** — Best for capturing quotes and milestones
     - Pros: Dead simple, family sharing, On This Day feature, free
     - Best for: Parents who want to save what kids say
   - **Photo journaling apps** — Best for visual memories
   - **Baby tracker apps** — Best for first-year milestones
   - **Family calendar apps** — Best for shared family organization
   - **Scrapbooking apps** — Best for creative parents

3. **How to Choose** (200 words)
   - Match the tool to your habit
   - Simpler = more likely to stick
   - Consider family sharing capabilities

4. **CTA**: Try KidQuotes free on the App Store

**SEO keywords**: best parenting apps, childhood memory apps, baby milestone tracker, family apps 2026`,
  },
];

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast({ title: 'Copied!' });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button variant="outline" size="sm" onClick={handleCopy}>
      {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
};

const ContentSection: React.FC<{ templates: ContentTemplate[] }> = ({ templates }) => (
  <div className="space-y-4">
    {templates.map((template, index) => (
      <Card key={index}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">{template.title}</CardTitle>
            <CopyButton text={template.content} />
          </div>
        </CardHeader>
        <CardContent>
          <pre className="text-sm whitespace-pre-wrap font-sans text-muted-foreground leading-relaxed">
            {template.content}
          </pre>
        </CardContent>
      </Card>
    ))}
  </div>
);

const ContentTools: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-3xl py-8 px-4">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">KidQuotes Content Tools</h1>
          <p className="text-muted-foreground mt-1">
            Ready-to-use content templates. Review, customize, and post.
          </p>
          <div className="flex gap-2 mt-3 text-xs text-muted-foreground">
            <span>App Store: <a href={APP_STORE_URL} className="underline">{APP_STORE_URL}</a></span>
            <span>|</span>
            <span>Site: <a href={SITE_URL} className="underline">{SITE_URL}</a></span>
          </div>
        </div>

        <Tabs defaultValue="instagram">
          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="instagram">Instagram</TabsTrigger>
            <TabsTrigger value="twitter">Twitter/X</TabsTrigger>
            <TabsTrigger value="reddit">Reddit</TabsTrigger>
            <TabsTrigger value="blog">Blog</TabsTrigger>
          </TabsList>

          <TabsContent value="instagram">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Instagram & Facebook posts. Replace example quotes with real ones from your family for authenticity.
              </p>
            </div>
            <ContentSection templates={instagramTemplates} />
          </TabsContent>

          <TabsContent value="twitter">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Twitter/X posts (under 280 characters where possible). Customize with your own kid quotes.
              </p>
            </div>
            <ContentSection templates={twitterTemplates} />
          </TabsContent>

          <TabsContent value="reddit">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Reddit discussion prompts for parenting communities. These are designed as genuine conversations, not ads. Customize with real quotes and experiences.
              </p>
            </div>
            <ContentSection templates={redditTemplates} />
          </TabsContent>

          <TabsContent value="blog">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Blog post outlines for content marketing and SEO. Flesh these out into full articles.
              </p>
            </div>
            <ContentSection templates={blogTemplates} />
          </TabsContent>
        </Tabs>
      </div>
      <Toaster />
    </div>
  );
};

export default ContentTools;
